import { Controller } from "@hotwired/stimulus"

// Propose l'installation de la PWA (modale) :
// - jamais si l'app tourne déjà en standalone (= déjà installée) ;
// - jamais tant que les modales de consentement ne sont pas passées
//   (pas d'empilement : elle apparaîtra à la visite suivante) ;
// - « Plus tard » → re-proposée 30 jours plus tard (localStorage) ;
// - installation acceptée/détectée → plus jamais.
// Android / Chrome desktop : vraie invite native via beforeinstallprompt.
// iOS Safari : pas d'API d'installation → instructions Partager →
// « Sur l'écran d'accueil ».
export default class extends Controller {
  static targets = ["installButton", "iosHelp"]
  static values = { key: { type: String, default: "champi_pwa_install_v1" } }

  static RETRY_DAYS = 30

  connect() {
    this.deferredPrompt = null
    if (this.standalone() || !this.consentDone() || this.snoozed()) return

    window.addEventListener("appinstalled", () => this.remember("installed"), { once: true })

    this._onBeforeInstallPrompt = (e) => {
      e.preventDefault() // on remplace la mini-barre native par notre modale
      this.deferredPrompt = e
      this.show({ ios: false })
    }
    window.addEventListener("beforeinstallprompt", this._onBeforeInstallPrompt)

    // iOS : beforeinstallprompt n'existe pas, on affiche les instructions
    if (this.isIos()) {
      this._iosTimer = setTimeout(() => this.show({ ios: true }), 2500)
    }
  }

  disconnect() {
    if (this._onBeforeInstallPrompt) window.removeEventListener("beforeinstallprompt", this._onBeforeInstallPrompt)
    clearTimeout(this._iosTimer)
  }

  show({ ios }) {
    if (this._shown) return
    this._shown = true
    this.iosHelpTarget.hidden = !ios
    this.installButtonTarget.hidden = ios
    this.element.classList.remove("hidden")
    this.element.classList.add("flex")
  }

  hide() {
    this.element.classList.add("hidden")
    this.element.classList.remove("flex")
  }

  async install() {
    this.hide()
    if (!this.deferredPrompt) return this.snooze()
    this.deferredPrompt.prompt()
    const { outcome } = await this.deferredPrompt.userChoice
    this.deferredPrompt = null
    outcome === "accepted" ? this.remember("installed") : this.snooze()
  }

  later() {
    this.hide()
    this.snooze()
  }

  // --- helpers ---

  snooze() {
    this.remember(`declined:${Date.now()}`)
  }

  snoozed() {
    try {
      const v = window.localStorage.getItem(this.keyValue)
      if (!v) return false
      if (v === "installed") return true
      const ts = parseInt(v.split(":")[1] || "0", 10)
      return Date.now() - ts < this.constructor.RETRY_DAYS * 24 * 60 * 60 * 1000
    } catch (e) {
      return true // localStorage indisponible (mode privé) : on n'insiste pas
    }
  }

  remember(val) {
    try { window.localStorage.setItem(this.keyValue, val) } catch (e) { /* tant pis */ }
  }

  consentDone() {
    try {
      return !!window.localStorage.getItem("champi_disclaimer_v1") &&
             !!window.localStorage.getItem("champi_cookies_v1")
    } catch (e) {
      return false
    }
  }

  standalone() {
    return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true
  }

  isIos() {
    return /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
      (window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1) // iPad "desktop mode"
  }
}
